import { dataVizKeyOf, type DataVizToneName } from "../../theme/dataVizPalette";
import type { DefaultTheme } from "styled-components";
import {
  MEDIA_TIMELINE_POLICY,
  mediaTimelineFraction,
} from "../MediaTimelineBar/mediaTimelineMath";
import type { NormalizedSeries } from "./seriesData";
import { segmentLineOf } from "../MediaTimelineBar/timelineSegmentPaint";
import {
  buildSeriesPath,
  SERIES_LINE_WIDTH_PX,
  type SeriesGeometry,
  type SeriesPathCommand,
} from "./seriesPath";
import { resolveCanvasToken } from "../MediaTimelineBar/timelineTickCanvas";
import type { MediaTimelineSegment } from "../MediaTimelineBar/timelineTypes";

/**
 * Ink line over a band: mostly the band's hue, pulled toward the ink so it still reads as a
 * line on the 30% tint beneath. Lower and the line sinks into a same-hue band; higher and the
 * hue hint disappears into plain ink.
 */
export const SERIES_BAND_HUE_PERCENT = 65;

/** Both the canvas and any consumer legend resolve the same theme token. */
export const seriesLineOf = (theme: DefaultTheme, tone: DataVizToneName): string =>
  theme.color.dataViz[dataVizKeyOf(tone)];

/** The ink line: a muted step so a single line stays lighter than text and the playhead. */
export const seriesInkOf = (theme: DefaultTheme): string => theme.color.text.muted;

/**
/**
 * Stroke alpha of a toned line by state. At rest it sits below full strength so the `dataViz.*`
 * hues leave room for what is drawn over the plot — playhead, trim edges, timeline handles.
 * An emphasized line rises to full strength while the others recede far enough that only it
 * reads at a glance, yet still trace their shape for comparison. Alpha rather than a fixed
 * lighter color keeps every hue and works over any surface. The ink line is already a muted
 * token and stays opaque at rest: a translucent ink stroke plus its band re-stroke would double
 * the coverage over the bands.
 */
export const SERIES_LINE_ALPHA = { rest: 0.8, emphasized: 1, receded: 0.3 } as const;

/**
 * Dash of a `dashed` line, in CSS px. Round caps add half the stroke to each dash end, so the
 * visible rhythm is 6 on / 4 off: long enough to read as a line at glance, short enough to
 * stay dashed through a 20px feature. Alpha states apply to a dashed line unchanged.
 */
export const SERIES_DASH_PX: readonly [number, number] = [4, 6];
const NO_DASH: number[] = [];

/** Tokens resolved during a paint, so an ancestor style change can be checked before repainting. */
export type SeriesPaintPalette = Map<string, string>;

/**
 * Stroke commands per series for one geometry. Emphasis, theme and band changes repaint with
 * the same paths; only a new viewport, size, domain or series walks the samples again.
 */
export interface SeriesPathCache {
  geometry: string;
  paths: Map<NormalizedSeries, SeriesPathCommand[]>;
}

export function createSeriesPathCache(): SeriesPathCache {
  return { geometry: "", paths: new Map() };
}

function geometryKey(geometry: SeriesGeometry): string {
  const { gutter, width, height, viewportStart, viewportDuration, domain } = geometry;
  return `${gutter ?? ""}|${width}|${height}|${viewportStart}|${viewportDuration}|${domain.min}|${domain.max}`;
}

/** Drops paths of series no longer painted, so replaced data is not kept alive by the cache. */
export function retainSeriesPaths(
  cache: SeriesPathCache,
  series: readonly NormalizedSeries[],
): void {
  for (const entry of cache.paths.keys()) {
    if (!series.includes(entry)) cache.paths.delete(entry);
  }
}

/** The cached path for this geometry, or a fresh one that the cache keeps. */
export function resolveSeriesPath(
  entry: NormalizedSeries,
  geometry: SeriesGeometry,
  cache: SeriesPathCache | undefined,
): SeriesPathCommand[] {
  if (!cache) return buildSeriesPath(entry, geometry);
  const key = geometryKey(geometry);
  if (cache.geometry !== key) {
    cache.geometry = key;
    cache.paths.clear();
  }
  let commands = cache.paths.get(entry);
  if (!commands) {
    commands = buildSeriesPath(entry, geometry);
    cache.paths.set(entry, commands);
  }
  return commands;
}

/**
 * Lines stroke in authored order; the last series paints on top. A toned line is one stroke in
 * its hue. An ink line strokes in ink, then once more per visible band clipped to the band, in
 * the band's hue pulled toward the ink — bands paint in their own order (active last) so an
 * overlapping band wins the same way it does beneath. A dashed line keeps one dash phase across
 * those re-strokes because every stroke walks the same path from its start. Nothing is drawn in
 * the gutter.
 *
 * With `emphasizedSeriesId`, the other lines stroke first at the receded alpha and the emphasized
 * line last at full strength, so no receded line covers it. A receded ink line strokes once,
 * without the band hue: a second translucent stroke would double its coverage over the bands and
 * read denser there than in the open.
 */
export function paintSeriesCanvas(
  context: CanvasRenderingContext2D,
  series: readonly NormalizedSeries[],
  options: Omit<SeriesGeometry, "domain"> & {
    domain: SeriesGeometry["domain"] | null;
    theme: DefaultTheme;
    /** Visible bands beneath the lines, already normalized and in paint order. */
    bands?: readonly MediaTimelineSegment[];
    /** Series kept at full strength while the others recede; an unknown id emphasizes nothing. */
    emphasizedSeriesId?: string | null;
    /**
     * The emphasized line sits in another lane of the same plot: every line here recedes, as the
     * other lines of its own lane do.
     */
    emphasisElsewhere?: boolean;
    /** Reuses paths across repaints of the same geometry (see `SeriesPathCache`). */
    pathCache?: SeriesPathCache;
  },
): SeriesPaintPalette {
  const { width, height, theme, domain, bands, viewportStart, viewportDuration } = options;
  const emphasized = series.find((entry) => entry.id === options.emphasizedSeriesId);
  const order = emphasized
    ? [...series.filter((entry) => entry !== emphasized), emphasized]
    : series;
  const gutter = options.gutter ?? MEDIA_TIMELINE_POLICY.leftGutterPx;
  const usableWidth = Math.max(0, width - gutter);
  context.clearRect(0, 0, width, height);
  const palette: SeriesPaintPalette = new Map();
  if (usableWidth === 0 || height <= 0 || !domain) return palette;
  const resolve = (token: string) => {
    let value = palette.get(token);
    if (value === undefined) {
      value = resolveCanvasToken(token, context.canvas);
      palette.set(token, value);
    }
    return value;
  };
  context.save();
  context.beginPath();
  context.rect(gutter, 0, usableWidth, height);
  context.clip();
  context.lineWidth = SERIES_LINE_WIDTH_PX;
  context.lineJoin = "round";
  context.lineCap = "round";
  if (options.pathCache) retainSeriesPaths(options.pathCache, series);
  for (const entry of order) {
    const commands = resolveSeriesPath(entry, { ...options, domain }, options.pathCache);
    if (commands.length === 0) continue;
    const receded =
      entry !== emphasized && (emphasized !== undefined || options.emphasisElsewhere === true);
    context.globalAlpha = receded
      ? SERIES_LINE_ALPHA.receded
      : entry === emphasized || entry.tone === undefined
        ? SERIES_LINE_ALPHA.emphasized
        : SERIES_LINE_ALPHA.rest;
    context.setLineDash(entry.lineStyle === "dashed" ? [...SERIES_DASH_PX] : NO_DASH);
    const trace = () => {
      context.beginPath();
      for (const command of commands) {
        if (command.op === "move") context.moveTo(command.x, command.y);
        else context.lineTo(command.x, command.y);
      }
      context.stroke();
    };
    if (entry.tone !== undefined) {
      context.strokeStyle = resolve(seriesLineOf(theme, entry.tone));
      trace();
      continue;
    }
    const ink = resolve(seriesInkOf(theme));
    context.strokeStyle = ink;
    trace();
    if (!bands || receded) continue;
    for (const active of [false, true]) {
      for (const band of bands) {
        if ((band.active === true) !== active) continue;
        const x =
          gutter +
          mediaTimelineFraction(band.startSeconds, viewportStart, viewportDuration) * usableWidth;
        const end =
          gutter +
          mediaTimelineFraction(band.endSeconds, viewportStart, viewportDuration) * usableWidth;
        if (end - x <= 0) continue;
        context.save();
        context.beginPath();
        context.rect(x, 0, end - x, height);
        context.clip();
        context.strokeStyle = `color-mix(in srgb, ${resolve(segmentLineOf(theme, band.tone))} ${SERIES_BAND_HUE_PERCENT}%, ${ink} ${100 - SERIES_BAND_HUE_PERCENT}%)`;
        trace();
        context.restore();
      }
    }
  }
  context.restore();
  return palette;
}
